import type { JourneyJson } from '../types/JourneyJson';
import type { LegJson } from '../types/LegJson';
import type { NearbyEdge, NearbyResponse, NearbyVertex } from './nearby-types';

/** Coordinate as `[lon, lat]` — GeoJSON's ordering, opposite to the wire's `[lat, lon]`. */
type LonLat = [number, number];

/** A single `LineString` feature in a GeoJSON `FeatureCollection`. */
interface LineFeature<Properties> {
  type: 'Feature';
  geometry: { type: 'LineString'; coordinates: LonLat[] };
  properties: Properties;
}

/** A single `Point` feature in a GeoJSON `FeatureCollection`. */
interface PointFeature<Properties> {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: LonLat };
  properties: Properties;
}

/** Union of feature variants either helper below can emit. */
type GeoJsonFeature =
  LineFeature<WalkLegProperties> | LineFeature<EdgeProperties> | PointFeature<VertexProperties>;

/** A homogeneous GeoJSON `FeatureCollection` produced by either helper below. */
export interface GeoJsonCollection {
  type: 'FeatureCollection';
  features: GeoJsonFeature[];
}

/** Feature properties emitted per walking leg by {@link journeyWalksToGeoJson}. */
export interface WalkLegProperties {
  /** Wall-clock walk duration for this leg (seconds). */
  walk_seconds: number;
  /** Physical walking distance for this leg (metres). */
  walk_meters: number;
  /** Fixed literal so consumers can style walking legs distinctly. */
  kind: 'walking_leg';
}

/** Feature properties emitted per vertex by {@link nearbyToGeoJson}. */
export interface VertexProperties {
  /** Flag labels set on this vertex (e.g. `"in_station"`, `"is_transit_entrance"`). */
  flags: string[];
  /** Vertical level; `null` when neither the node nor its way supplied one. */
  level: number | null;
  /** Haversine distance in metres from the query centre. */
  distance_m: number;
  /** Fixed literal so consumers can style vertex points distinctly. */
  kind: 'vertex';
}

/** Feature properties emitted per outgoing edge by {@link nearbyToGeoJson}. */
export interface EdgeProperties {
  /** Surface classification (see {@link NearbyEdge}). */
  surface: string;
  /** Surface-adjusted traversal time in milliseconds. */
  cost_ms: number;
  /** Haversine distance in millimetres. */
  distance_mm: number;
  /** Fixed literal so consumers can style graph edges distinctly. */
  kind: 'edge';
}

/** True when `legItem` is a walking leg (used to narrow the `LegJson` union). */
function isWalkingLeg(legItem: LegJson): legItem is Extract<LegJson, { type: 'walking' }> {
  return legItem.type === 'walking';
}

/** Reverses each `[lat, lon]` pair to GeoJSON's `[lon, lat]` order. */
function toLonLatPairs(latLonPairs: readonly [number, number][]): LonLat[] {
  return latLonPairs.map(([latitude, longitude]) => [longitude, latitude]);
}

/** Fallback two-point line for a walking leg with an empty geometry array. */
function fallbackWalkingCoords(
  walkingLeg: Extract<LegJson, { type: 'walking' }>,
): [number, number][] {
  return [
    [walkingLeg.from.lat, walkingLeg.from.lon],
    [walkingLeg.to.lat, walkingLeg.to.lon],
  ];
}

/** Prefers the routed geometry when it has ≥ 2 vertices; otherwise the from/to fallback. */
function walkingCoords(walkingLeg: Extract<LegJson, { type: 'walking' }>): [number, number][] {
  if (walkingLeg.geometry.length >= 2) return walkingLeg.geometry;
  return fallbackWalkingCoords(walkingLeg);
}

/** Builds one GeoJSON `LineString` feature for a walking leg. */
function walkingLegFeature(
  walkingLeg: Extract<LegJson, { type: 'walking' }>,
): LineFeature<WalkLegProperties> {
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: toLonLatPairs(walkingCoords(walkingLeg)) },
    properties: {
      walk_seconds: walkingLeg.walk_seconds,
      walk_meters: walkingLeg.walk_meters,
      kind: 'walking_leg',
    },
  };
}

/**
 * Converts every walking leg of `journey` to a GeoJSON `FeatureCollection`.
 * Transit legs are skipped; an empty legs array yields an empty collection.
 */
export function journeyWalksToGeoJson(journey: JourneyJson): GeoJsonCollection {
  const features: GeoJsonFeature[] = journey.legs
    .filter((legItem) => isWalkingLeg(legItem))
    .map((walkingLeg) => walkingLegFeature(walkingLeg));
  return { type: 'FeatureCollection', features };
}

/** Builds one GeoJSON `Point` feature for a vertex. */
function vertexFeature(vertex: NearbyVertex): PointFeature<VertexProperties> {
  const [latitude, longitude] = vertex.coord;
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [longitude, latitude] },
    properties: {
      flags: [...vertex.flags],
      level: vertex.level,
      distance_m: vertex.distance_m,
      kind: 'vertex',
    },
  };
}

/** Builds one GeoJSON `LineString` feature for an outgoing edge from `vertex`. */
function edgeFeature(vertex: NearbyVertex, edge: NearbyEdge): LineFeature<EdgeProperties> {
  const [fromLat, fromLon] = vertex.coord;
  const [toLat, toLon] = edge.to_coord;
  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: [
        [fromLon, fromLat],
        [toLon, toLat],
      ],
    },
    properties: {
      surface: edge.surface,
      cost_ms: edge.cost_ms,
      distance_mm: edge.distance_mm,
      kind: 'edge',
    },
  };
}

/** Every outgoing edge from `vertex`, materialised as `LineString` features. */
function edgeFeatures(vertex: NearbyVertex): LineFeature<EdgeProperties>[] {
  return vertex.edges.map((edge) => edgeFeature(vertex, edge));
}

/**
 * Converts a `NearbyResponse` to a GeoJSON `FeatureCollection` with one `Point`
 * per vertex and one `LineString` per outgoing edge. An empty vertices array
 * yields an empty collection.
 */
export function nearbyToGeoJson(response: NearbyResponse): GeoJsonCollection {
  const features: GeoJsonFeature[] = [];
  for (const vertex of response.vertices) {
    features.push(vertexFeature(vertex));
    for (const feature of edgeFeatures(vertex)) features.push(feature);
  }
  return { type: 'FeatureCollection', features };
}

/**
 * Serialises `collection` to a pretty-printed JSON string, defaulting to two
 * spaces of indent so the download is easy to compare against a reference.
 */
export function geoJsonToString(collection: GeoJsonCollection): string {
  return JSON.stringify(collection, null, 2);
}
